import { Component, inject, OnInit, OnDestroy, signal, effect } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Tag } from 'primeng/tag';
import { Router } from '@angular/router';
import { SortEvent } from 'primeng/api';
import { PaginatorState } from 'primeng/paginator';
import { finalize, Subject, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {
  DynamicFilterConfig,
  DynamicFilterValues,
  EnumValues,
  FilterOption,
  GlobalEntityDefinition,
  createDynamicFilterState,
} from '@card-software/library';
import { LocalHelperService } from '../../../../../services/local-helper.service';
import { EnumService } from '../../../../../services/enum.service';
import { ProviderReturnProxyService } from '../../../../../proxies/inventory/provider-return.proxy.service';
import { ProviderReturn, ProviderReturnStatus } from '../../../../../models/inventory/provider-return';
import { ProviderReturnSearchCommand } from '../../../../../models/inventory/provider-return-search';
import { GoodsReceiptProxyService } from '../../../../../proxies/inventory/goods-receipt.proxy.service';
import { SupplyWarehouseDocumentType } from '../../../../../models/supply-warehouse/supply-warehouse-document-type.enum';
import { globalSortFunction } from '../../../../../utils/global-sort.util';

@Component({
  selector: 'app-provider-return-tab',
  templateUrl: './provider-return-tab.component.html',
  styleUrl: './provider-return-tab.component.scss',
})
export class ProviderReturnTabComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private translateService = inject(TranslateService);
  private enumService = inject(EnumService);
  private providerReturnService = inject(ProviderReturnProxyService);
  private goodsReceiptService = inject(GoodsReceiptProxyService);
  public localHelperService = inject(LocalHelperService);

  private destroy$ = new Subject<void>();
  private appliedFilters: DynamicFilterValues = {};

  readonly LOCATION_STORAGE_KEY = 'supply-warehouse-selected-location';
  readonly CONSUMABLES_CODE = 'NT.WH-CON-1';

  isLoading = signal<number>(0);
  showFilters = signal<boolean>(true);
  providerReturnList = signal<ProviderReturn[]>([]);

  filterConfig = signal<DynamicFilterConfig | null>(null);
  readonly filterState = createDynamicFilterState({
    storageKey: 'supply-warehouse-provider-returns-filters',
    config: this.filterConfig,
    paramPrefix: 'pr.',
  });
  statusesPR = signal<EnumValues>({});
  enumsReady = signal(false);

  totalRecords = 0;
  currentPage = 1;
  pageSize = 25;

  private getTranslatedStatusOptions(): FilterOption[] {
    const statuses = this.statusesPR();
    return Object.keys(statuses)
      .filter((key) => !isNaN(Number(key)))
      .map((key) => {
        const numKey = Number(key);
        const enumName = ProviderReturnStatus[numKey];
        const translationKey = `Common_Status_${enumName}`;
        const translated = this.translateService.instant(translationKey);
        return {
          value: numKey,
          label: translated !== translationKey ? translated : (statuses[numKey] as string),
        };
      });
  }

  get selectedLocation(): string {
    return localStorage.getItem(this.LOCATION_STORAGE_KEY) || this.CONSUMABLES_CODE;
  }

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => this.searchWithFilters(filters));

    effect(
      () => {
        const statusValues = this.enumService.getEnum('ProviderReturnStatus', 'inventory').values;
        if (Object.keys(statusValues).length > 0) {
          this.statusesPR.set(statusValues);
          this.enumsReady.set(true);
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        if (this.translateService.currentLang && this.enumsReady()) {
          this.tryInitializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => this.tryInitializeFilterConfig());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private tryInitializeFilterConfig(): void {
    if (this.translateService.currentLang && this.enumsReady()) {
      this.initializeFilterConfig();
    }
  }

  private initializeFilterConfig(): void {
    const translate = (key: string, fallback: string) => {
      const t = this.translateService.instant(key);
      return t === key ? fallback : t;
    };

    const config: DynamicFilterConfig = {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'number',
          label: translate('PR_Number', 'PR Number'),
          placeholder: translate('Search_PR_number', 'Search PR number'),
          defaultVisible: true,
        },
        {
          type: 'text',
          formControlName: 'locationCode',
          label: translate('Common_Location', 'Location'),
          placeholder: translate('Search_location', 'Search location'),
          defaultVisible: false,
        },
        {
          type: 'multiselect',
          formControlName: 'statuses',
          label: translate('Common_Status', 'Status'),
          placeholder: translate('Select_status', 'Select status'),
          options: this.getTranslatedStatusOptions(),
          showClear: true,
          defaultVisible: false,
        },
        {
          type: 'text',
          formControlName: 'goodsReceiptNumber',
          label: translate('Goods_Receipt_Number', 'Goods Receipt Number'),
          placeholder: translate('Search_GRN_number', 'Search GRN number'),
          defaultVisible: false,
        },
      ],
    };

    this.filterConfig.set(config);
  }

  search(): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const formValues: any = this.appliedFilters;
    const number = formValues.number?.trim();
    const locationCode = formValues.locationCode?.trim();
    const goodsReceiptNumber = formValues.goodsReceiptNumber?.trim();

    const statusesRaw = formValues.statuses;
    const statuses = Array.isArray(statusesRaw)
      ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
        statusesRaw.map((s: any) => (typeof s === 'object' && s.value !== undefined ? s.value : s))
      : [];

    const baseCommand: ProviderReturnSearchCommand = {
      number: number || undefined,
      locationCode: locationCode || this.selectedLocation,
      status: statuses.length === 1 ? statuses[0] : undefined,
    };

    // ProviderReturn.initiator -> GoodsReceipt. The API has no goodsReceiptNumber param, but it does
    // filter by `initiator`. So resolve the GRN number to its id first, then filter by initiator
    // (server-side pagination/totals stay correct).
    if (goodsReceiptNumber) {
      this.isLoading.update((v) => v + 1);
      this.goodsReceiptService
        .search({ number: goodsReceiptNumber })
        .pipe(finalize(() => this.isLoading.update((v) => v - 1)))
        .subscribe({
          next: (response) => {
            const grnId = response.data?.find((g) => g.number === goodsReceiptNumber)?.id ?? response.data?.[0]?.id;
            if (!grnId) {
              this.providerReturnList.set([]);
              this.totalRecords = 0;
              return;
            }
            this.executeSearch({
              ...baseCommand,
              initiator: { entityDefinitionCode: GlobalEntityDefinition.GoodsReceipt, reference: grnId },
            });
          },
          error: () => {
            this.providerReturnList.set([]);
            this.totalRecords = 0;
          },
        });
      return;
    }

    this.executeSearch(baseCommand);
  }

  private executeSearch(command: ProviderReturnSearchCommand): void {
    this.isLoading.update((v) => v + 1);
    this.providerReturnService
      .search(command, this.currentPage, this.pageSize)
      .pipe(finalize(() => this.isLoading.update((v) => v - 1)))
      .subscribe({
        next: (response) => {
          this.providerReturnList.set(response.data?.items ?? []);
          this.totalRecords = response.data?.totalCount ?? 0;
        },
        error: () => {
          this.providerReturnList.set([]);
          this.totalRecords = 0;
        },
      });
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage = (event.page ?? 0) + 1;
    this.pageSize = event.rows ?? this.pageSize;
    this.search();
  }

  private searchWithFilters(filters: DynamicFilterValues): void {
    this.appliedFilters = filters;
    this.currentPage = 1;
    this.search();
  }

  onTableSort(event: SortEvent): void {
    globalSortFunction<ProviderReturn>(event, ['number']);
  }

  getStatusLabel(status: ProviderReturnStatus): string {
    const key = `Common_Status_${ProviderReturnStatus[status]}`;
    const result = this.translateService.instant(key);
    if (result === key) {
      return (this.statusesPR()[status] as string) || '';
    }
    return result;
  }

  getStatusSeverity(status: ProviderReturnStatus): Tag['severity'] {
    switch (status) {
      case ProviderReturnStatus.Pending:
        return 'warn';
      case ProviderReturnStatus.Processing:
        return 'info';
      case ProviderReturnStatus.Completed:
        return 'success';
      case ProviderReturnStatus.Cancelled:
      case ProviderReturnStatus.Rejected:
        return 'danger';
      default:
        return undefined;
    }
  }

  navigateToDetail(grnId: string): void {
    this.router.navigate(['/supply-warehouse-detail'], {
      queryParams: {
        documentType: SupplyWarehouseDocumentType.ProviderReturn,
        id: grnId,
      },
    });
  }
}
